import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { OpenSlot } from '@regis/shared';
import { api } from '@/lib/api';
import { Button, Muted } from '@/components/ui';

/**
 * Day-first booking picker — the touch-friendly replacement for the horizontal
 * drag grid.
 *
 *   1. A horizontal DATE STRIP (a marker dot = the coach has openings that day).
 *   2. Tapping a day shows that day's open slots as full-width, finger-sized
 *      BUTTONS with the time spelled out — no axis to line up, no sideways drag.
 *   3. Tapping a slot adds it to a SELECTION CART (tap again to remove). Slots
 *      from multiple days accumulate; the cart bar shows count + credit cost +
 *      the client's balance.
 *   4. An optional "repeat weekly × N" turns every cart slot into a recurring
 *      series. CONFIRM books the whole cart atomically and deducts credits.
 *
 * In RESCHEDULE mode (rescheduleApptId set) it's single-pick, no cart, no charge
 * (same row keeps its credit): tapping a slot immediately PATCHes the booking.
 */

const DAYS = 14;
const DEFAULT_DURATION = 60;

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}
function dayParts(iso: string): { wd: string; day: string; full: string } {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return {
    wd: dt.toLocaleDateString(undefined, { weekday: 'short' }),
    day: dt.toLocaleDateString(undefined, { day: 'numeric' }),
    full: dt.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' }),
  };
}
function label12(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${ampm}` : `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}
function slotKey(s: { date: string; start_time: string }): string {
  return `${s.date}T${s.start_time}`;
}

interface CartSlot {
  date: string;
  start_time: string;
  end_time: string;
}

export function BookingPicker({
  rescheduleApptId,
  onDone,
}: { rescheduleApptId?: string; onDone?: () => void } = {}) {
  const qc = useQueryClient();
  const isReschedule = !!rescheduleApptId;

  const [start, setStart] = useState(todayLocal());
  const [duration, setDuration] = useState(DEFAULT_DURATION);
  const [activeDay, setActiveDay] = useState(todayLocal());
  const [cart, setCart] = useState<CartSlot[]>([]);
  const [repeatWeekly, setRepeatWeekly] = useState(false);
  const [repeatCount, setRepeatCount] = useState(4);
  const [error, setError] = useState<string | null>(null);

  const dates = useMemo(() => Array.from({ length: DAYS }, (_, i) => addDays(start, i)), [start]);

  // Keep the active day's tile visible in the (horizontally-scrolling) strip.
  // Without this, paging the window or hitting "Today" leaves the selected tile
  // scrolled off-screen — the strip's manual scroll position never resets.
  const stripRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    const tile = strip.querySelector<HTMLElement>(`[data-day="${activeDay}"]`);
    if (tile) tile.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [activeDay, dates]);

  // Move the window by a week and follow it with the active day, so the selected
  // tile and the visible strip never drift apart.
  function shiftWindow(deltaDays: number) {
    const nextStart = addDays(start, deltaDays);
    setStart(nextStart);
    setActiveDay(addDays(activeDay, deltaDays));
  }
  function goToday() {
    const t = todayLocal();
    setStart(t);
    setActiveDay(t);
  }

  const slots = useQuery({
    queryKey: ['slots', start, duration],
    queryFn: () =>
      api.get<{ slots: OpenSlot[]; duration: number }>(
        `/api/appointments/slots?days=${DAYS}&duration=${duration}`
      ),
  });

  // What booking costs (per-session price + current balance). Skipped in reschedule.
  const cost = useQuery({
    queryKey: ['booking-cost'],
    queryFn: () =>
      api.get<{ credit_cost_per_session: number; balance: number; recurring_max_count: number }>(
        '/api/appointments/cost'
      ),
    enabled: !isReschedule,
  });

  // Open slots grouped by date, and the set of days that have any opening.
  const slotsByDate = useMemo(() => {
    const map: Record<string, OpenSlot[]> = {};
    for (const s of slots.data?.slots ?? []) (map[s.date] ??= []).push(s);
    return map;
  }, [slots.data]);

  const daySlots = slotsByDate[activeDay] ?? [];
  const cartKeys = useMemo(() => new Set(cart.map(slotKey)), [cart]);

  const perSession = cost.data?.credit_cost_per_session ?? 0;
  const balance = cost.data?.balance ?? 0;
  const recurringMax = cost.data?.recurring_max_count ?? 12;
  const sessionsCount = cart.length * (repeatWeekly ? repeatCount : 1);
  const totalCost = sessionsCount * perSession;
  const shortBy = Math.max(0, totalCost - balance);

  function toggleSlot(s: OpenSlot) {
    setError(null);
    if (isReschedule) {
      // Single-pick: PATCH immediately.
      reschedule.mutate({ date: s.date, start_time: s.start_time, end_time: s.end_time });
      return;
    }
    const k = slotKey(s);
    setCart((prev) =>
      cartKeys.has(k)
        ? prev.filter((c) => slotKey(c) !== k)
        : [...prev, { date: s.date, start_time: s.start_time, end_time: s.end_time }]
    );
  }

  const book = useMutation({
    mutationFn: () =>
      api.post<{ booked: number; credits_spent: number; balance: number }>('/api/appointments/batch', {
        slots: cart,
        repeat: repeatWeekly ? { weekly: true, count: repeatCount } : undefined,
      }),
    onSuccess: () => {
      setCart([]);
      setRepeatWeekly(false);
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: ['slots'] });
      qc.invalidateQueries({ queryKey: ['booking-cost'] });
    },
    onError: (e: unknown) => {
      const code = (e as { code?: string })?.code;
      if (code === 'insufficient_credits') setError('insufficient');
      else if (code === 'slot_taken') setError('taken');
      else if (code === 'no_coach_configured') setError('no_coach');
      else if (code === 'too_many') setError('too_many');
      else setError(code ? `generic:${code}` : 'generic');
    },
  });

  const reschedule = useMutation({
    mutationFn: (s: { date: string; start_time: string; end_time: string }) =>
      api.patch(`/api/appointments/${rescheduleApptId}`, s),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['appointments'] });
      qc.invalidateQueries({ queryKey: ['slots'] });
      onDone?.();
    },
    onError: () => setError('taken'),
  });

  const busy = book.isPending || reschedule.isPending;

  return (
    <div style={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
      {/* Duration toggle + window nav */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <div style={{ display: 'inline-flex', borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--card-border)' }}>
          {[30, 60].map((d) => (
            <button
              key={d}
              onClick={() => setDuration(d)}
              style={{
                padding: '0.4rem 0.9rem',
                fontSize: '0.82rem',
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                background: duration === d ? 'var(--gold)' : 'transparent',
                color: duration === d ? '#1a1a1a' : 'var(--text-tertiary)',
              }}
            >
              {d} min
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          <Button variant="ghost" onClick={() => shiftWindow(-DAYS)} aria-label="Previous two weeks">&larr;</Button>
          <Button variant="ghost" onClick={goToday}>Today</Button>
          <Button variant="ghost" onClick={() => shiftWindow(DAYS)} aria-label="Next two weeks">&rarr;</Button>
        </div>
      </div>

      {/* Date strip — scrolls horizontally on its own; minWidth:0 lets it shrink
          inside the clamped page column instead of widening the whole page. */}
      <div ref={stripRef} style={{ display: 'flex', gap: '0.4rem', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '0.5rem', scrollbarWidth: 'thin', minWidth: 0, maxWidth: '100%' }}>
        {dates.map((d) => {
          const { wd, day } = dayParts(d);
          const hasOpenings = (slotsByDate[d]?.length ?? 0) > 0;
          const isActive = d === activeDay;
          const isToday = d === todayLocal();
          const inCart = cart.some((c) => c.date === d);
          return (
            <button
              key={d}
              data-day={d}
              onClick={() => setActiveDay(d)}
              aria-pressed={isActive}
              style={{
                flex: '0 0 auto',
                width: 52,
                padding: '0.5rem 0',
                borderRadius: 'var(--radius-sm)',
                border: `1px solid ${isActive ? 'var(--gold)' : 'var(--card-border)'}`,
                background: isActive ? 'var(--gold)' : 'var(--card-bg)',
                color: isActive ? '#1a1a1a' : isToday ? 'var(--gold)' : 'var(--text-primary)',
                cursor: 'pointer',
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <div style={{ fontSize: '0.66rem', textTransform: 'uppercase', opacity: 0.8 }}>{wd}</div>
              <div style={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.1 }}>{day}</div>
              <div style={{ height: 6, marginTop: 2, display: 'flex', justifyContent: 'center', gap: 3 }}>
                {hasOpenings && (
                  <span style={{ width: 5, height: 5, borderRadius: 999, background: isActive ? '#1a1a1a' : 'var(--success)' }} />
                )}
                {inCart && (
                  <span style={{ width: 5, height: 5, borderRadius: 999, background: isActive ? '#1a1a1a' : 'var(--gold)' }} />
                )}
              </div>
            </button>
          );
        })}
      </div>

      {/* Selected day's slots */}
      <div style={{ marginTop: '0.75rem' }}>
        <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>{dayParts(activeDay).full}</div>
        {slots.isLoading ? (
          <Muted>Loading availability…</Muted>
        ) : daySlots.length === 0 ? (
          <Muted>No openings this day. Try another date above.</Muted>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(104px, 1fr))', gap: '0.5rem', width: '100%' }}>
            {daySlots.map((s) => {
              const selected = cartKeys.has(slotKey(s));
              return (
                <button
                  key={slotKey(s)}
                  onClick={() => toggleSlot(s)}
                  aria-pressed={selected}
                  style={{
                    minWidth: 0,
                    minHeight: 52,
                    padding: '0.5rem 0.4rem',
                    borderRadius: 'var(--radius-sm)',
                    border: `1px solid ${selected ? 'var(--gold)' : 'var(--card-border)'}`,
                    background: selected ? 'var(--gold)' : 'var(--card-bg)',
                    color: selected ? '#1a1a1a' : 'var(--text-primary)',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: 1.15,
                  }}
                >
                  {/* Start time prominent, end time small beneath — never wraps. */}
                  <span style={{ fontSize: '0.92rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{label12(s.start_time)}</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 500, opacity: selected ? 0.7 : 0.6, whiteSpace: 'nowrap' }}>
                    to {label12(s.end_time)}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Recurrence + cart + confirm (booking mode only) */}
      {!isReschedule && cart.length > 0 && (
        <div
          style={{
            marginTop: '1rem',
            padding: '0.85rem 1rem',
            background: 'var(--card-bg)',
            border: '1px solid var(--gold)',
            borderRadius: 'var(--radius-sm)',
            display: 'grid',
            gap: '0.75rem',
          }}
        >
          {/* Cart chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {cart.map((c) => (
              <span
                key={slotKey(c)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: '0.74rem',
                  padding: '0.25rem 0.5rem',
                  borderRadius: 999,
                  background: 'rgba(201,168,106,0.15)',
                  border: '1px solid var(--gold-muted)',
                }}
              >
                {dayParts(c.date).wd} {label12(c.start_time)}
                <button
                  onClick={() => setCart((prev) => prev.filter((x) => slotKey(x) !== slotKey(c)))}
                  aria-label="Remove"
                  style={{ border: 'none', background: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: '0.9rem', lineHeight: 1 }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>

          {/* Repeat weekly */}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.84rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={repeatWeekly} onChange={(e) => setRepeatWeekly(e.target.checked)} />
            Repeat weekly
            {repeatWeekly && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginLeft: '0.25rem' }}>
                ×
                <select
                  value={repeatCount}
                  onChange={(e) => setRepeatCount(Number(e.target.value))}
                  style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '0.15rem 0.35rem' }}
                >
                  {Array.from({ length: recurringMax }, (_, i) => i + 1)
                    .filter((n) => n >= 2)
                    .map((n) => (
                      <option key={n} value={n}>{n} weeks</option>
                    ))}
                </select>
              </span>
            )}
          </label>

          {/* Cost summary */}
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
            <div>
              <div style={{ fontWeight: 600 }}>
                {sessionsCount} session{sessionsCount === 1 ? '' : 's'}
                {perSession > 0 && ` · ${totalCost} credit${totalCost === 1 ? '' : 's'}`}
              </div>
              {perSession > 0 && (
                <Muted style={{ fontSize: '0.76rem' }}>
                  You have {balance} credit{balance === 1 ? '' : 's'}
                  {shortBy > 0 ? ` · short ${shortBy}` : ''}
                </Muted>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button variant="ghost" onClick={() => { setCart([]); setRepeatWeekly(false); }} disabled={busy}>Clear</Button>
              <Button onClick={() => book.mutate()} disabled={busy || (perSession > 0 && shortBy > 0)}>
                {busy ? 'Booking…' : 'Confirm booking'}
              </Button>
            </div>
          </div>

          {error === 'insufficient' && (
            <Muted style={{ color: 'var(--error)' }}>
              Not enough credits. Buy or redeem more in your Wallet, then confirm.
            </Muted>
          )}
          {error === 'taken' && (
            <Muted style={{ color: 'var(--error)' }}>One of those times was just taken — remove it and pick another.</Muted>
          )}
          {error === 'no_coach' && (
            <Muted style={{ color: 'var(--error)' }}>No coach is assigned to your account yet. Contact your coach.</Muted>
          )}
          {error === 'too_many' && (
            <Muted style={{ color: 'var(--error)' }}>That&apos;s more sessions than one booking allows. Remove a few and retry.</Muted>
          )}
          {error?.startsWith('generic') && (
            <Muted style={{ color: 'var(--error)' }}>
              Booking failed{error.includes(':') ? ` (${error.split(':')[1]})` : ''} — please retry.
            </Muted>
          )}
        </div>
      )}

      {isReschedule && error === 'taken' && (
        <Muted style={{ color: 'var(--error)', marginTop: '0.75rem', display: 'block' }}>
          That time isn’t available anymore — pick another.
        </Muted>
      )}
    </div>
  );
}
